<template>
  <div
    class="search-form-container"
    @keydown.38="moveUp"
    @keydown.40="moveDown"
  >
    <section id="suggestion-box">
      <span id="prefixbox">{{ searchValue }}</span>
      <span id="suffixbox">{{ suffix }}</span>
    </section>
    <form @submit.prevent="userDidPressEnter">
      <text-input
        id="searchBox"
        ref="searchBox"
        v-model="searchValue"
        autocomplete="off"
        :title-label="$t('input-placeholder')"
        @blur="onBlur"
      >
      </text-input>
    </form>
    <span id="search-icon-box">
      <dotcom-icon
        v-show="!searchIconHidden"
        name="magnifying-glass"
        art="light"
        color="--c-primary-neutral-3"
        hover-color="--c-primary-neutral-3"
        @click="userDidPressEnter"
      />
      <dotcom-icon
        v-show="!resetIconHidden"
        name="xmark"
        art="light"
        color="--c-primary-neutral-3"
        hover-color="--c-primary-neutral-3"
        @click="userDidPressClear"
      />
    </span>
    <div v-show="displaySearchResults" class="search-result-wrapper">
      <div v-for="(choice, i) in formattedChoices" id="resultList" :key="i">
        <div class="sanitas-form-search" @click="selectItem(choice)">
          <sanitas-form-search-suggestion
            class="is-negative"
            :class="{
              selected: selected === i,
            }"
            :suggestion="choice"
            :search-value="searchValue.trim()"
          ></sanitas-form-search-suggestion>
        </div>
        <input name="searchInput" type="hidden" :value="localValue" />
      </div>
    </div>
    <div v-show="showNoSuggestionSelectedPane" id="noSuggestionSelected">
      {{ $t('no-suggestion-selected-label') }}
    </div>
    <div v-if="showStaticSuggestions" class="search-form__static-suggestions">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { useI36n } from '@jota-one/i36n/src/index.vue2'

import { client } from '@/helpers/http'

import SanitasFormSearchSuggestion from './SanitasFormSearchSuggestion'

export default {
  name: 'SanitasFormSearch',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    TextInput: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/TextInput'
      )
    },
    SanitasFormSearchSuggestion,
  },

  props: {
    autocompleteSearchEndpointUrl: {
      type: String,
      default: '',
    },

    minimumSearchLength: {
      type: Number,
      default: 3,
    },

    maxVisibleSuggestions: {
      type: Number,
      default: 10,
    },

    comboboxData: {
      type: Array,
      default: () => [],
    },

    shouldShowResults: Boolean,
  },

  setup() {
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },

  data: () => ({
    searchValue: '',
    suffix: '',
    searchIconHidden: false,
    showNoSuggestionSelectedPane: false,
    displaySearchResults: false,
    resetIconHidden: true,
    requestTimeoutId: null,
    selected: -1,
    suggestions: [],
    choices: [],
    localValue: '',
  }),

  computed: {
    formattedChoices() {
      return this.choices.filter(this.filterSuggestions)
    },
    showStaticSuggestions() {
      return !this.shouldShowResults && !this.suggestions.length
    },
  },
  watch: {
    searchValue(newValue, oldValue) {
      this.showNoSuggestionSelectedPane = false
      if (newValue === undefined) {
        return
      }
      if (newValue.length < this.minimumSearchLength) {
        this.suffix = ''
        this.displaySearchResults = false
        this.suggestions = []
        return
      }
      this.suggestions = []
      this.selected = -1
      this.suffix = ''
      // If the first [minimumSearchLength] chars are different from the oldValue
      // we cancel any running query
      const oldValueStripped = oldValue.substring(0, this.minimumSearchLength)
      const newValueStripped = newValue.substring(0, this.minimumSearchLength)

      if (this.requestTimeoutId && oldValueStripped !== newValueStripped) {
        clearTimeout(this.requestTimeoutId)
      }

      if (newValue === this.localValue) {
        return
      }
      this.requestTimeoutId = setTimeout(() => {
        if (this.autocompleteSearchEndpointUrl) {
          this.fetchSuggestions().then(this.showResults)
        } else {
          const updateChoices =
            this.choices.length === 0 ||
            this.choices[0] !== this.comboboxData[0]
          if (this.comboboxData && updateChoices) {
            this.choices = this.comboboxData
          }
          this.showResults()
        }
      }, 50)
    },
    displaySearchResults(value) {
      this.$emit('suggestionsOpened', value)
    },
  },

  mounted() {
    const element = document.getElementById('searchBox')
    if (element) {
      element.getElementsByTagName('input')[0].addEventListener(
        'keydown',
        e => {
          if (e.keyCode === 9) {
            this.userDidPressTab(e)
          } else if (e.keyCode === 13) {
            this.userDidPressEnter()
          }
        },
        false,
      )
    }
  },
  methods: {
    moveUp() {
      this.selected =
        this.selected > 0 ? this.selected - 1 : this.formattedChoices.length - 1
      this.updateInputSuffix()
    },
    moveDown() {
      this.selected =
        this.selected < this.formattedChoices.length - 1 ? this.selected + 1 : 0
      this.updateInputSuffix()
    },
    updateInputSuffix() {
      const { selected, formattedChoices, searchValue } = this
      let choice =
        selected > -1 && selected < formattedChoices.length
          ? formattedChoices[selected]
          : ''
      const suffix = choice.split(searchValue)
      this.suffix = suffix[1] || ''
    },
    onBlur() {
      if (this.displaySearchResults) {
        return
      }

      const userDidTapOutside = this.localValue === undefined

      if (userDidTapOutside) {
        this.displaySearchResults = false
        this.suffix = ''
      }
    },
    userDidPressTab(e) {
      e.preventDefault()
      if (this.suggestions.length > 0 && this.searchValue !== undefined) {
        this.searchValue += this.suffix
        this.displaySearchResults = false
        this.focusInput()
      }
    },
    userDidPressEnter() {
      if (this.searchValue === undefined) {
        return
      }

      if (this.searchValue.length >= this.minimumSearchLength) {
        const { selected, formattedChoices } = this
        if (selected > -1 && selected < formattedChoices.length) {
          this.selectItem(formattedChoices[selected])
        } else {
          this.userDidPressSearch(this.searchValue)
          this.suffix = ''
          this.displaySearchResults = false
        }
      }
    },
    userDidPressClear() {
      this.searchValue = undefined
      this.suffix = ''
      this.suggestions = []
      this.choices = []
      this.selected = -1
      this.toggleSearchIcons()
    },
    focusInput: function () {
      setTimeout(() => {
        this.$refs.searchBox.focus()
      }, 100)
    },
    userDidPressSearch(value) {
      this.$emit('sanitas_search_search', {
        value: value,
        suggestions: this.suggestions,
      })

      this.$refs.searchBox.$refs.inputfield.blur()
      this.toggleSearchIcons()
    },
    toggleSearchIcons() {
      if (
        this.searchValue !== undefined &&
        this.searchValue.length >= this.minimumSearchLength
      ) {
        this.searchIconHidden = true
        this.resetIconHidden = false
      } else {
        this.searchIconHidden = false
        this.resetIconHidden = true
      }
    },
    filterSuggestions(choice) {
      if (!choice) {
        return false
      }

      let match = false
      const searchValue = this.searchValue.trim()

      // no item is displayed when the input has not reached the minimum length
      if (searchValue.length < this.minimumSearchLength) {
        return false
      }

      const matchPos = function (str) {
        return str.toLowerCase().indexOf(searchValue.toLowerCase())
      }

      const matchCurrentPos = matchPos(choice)
      let matchCondition =
        matchCurrentPos === 0 || choice[matchCurrentPos - 1] === ' '

      if (matchCondition) {
        this.suggestions.push(choice)
        match = true
      }

      if (match) {
        const suggestion = this.suggestions[0]
        this.suffix = suggestion.substr(
          matchPos(suggestion) + searchValue.length,
        )

        return this.suggestions.length <= this.maxVisibleSuggestions
      }

      return false
    },
    selectItem(choice) {
      this.localValue = choice
      this.userDidPressSearch(this.localValue)
      this.searchValue = choice
      this.suffix = ''
      this.displaySearchResults = false
    },
    showResults() {
      this.suggestions = []

      // we have nothing to show yet
      if (this.choices === undefined) {
        return
      }
      this.displaySearchResults = true
    },
    fetchSuggestions() {
      if (this.searchValue.length !== this.minimumSearchLength) {
        return new Promise(resolve => {
          resolve(null)
        })
      }
      const params = {}
      params.q = this.searchValue.toLowerCase()
      params._charset_ = 'utf-8'
      return client()
        .get(this.autocompleteSearchEndpointUrl, { params })
        .then(({ data }) => {
          this.choices = data.results
        })
        .catch(err => {
          console.log(err)
          this.choices = [
            'forms',
            'formular',
            'formulars',
            'for',
            'form',
            'format',
          ]
        })
    },
  },
}
</script>

<style lang="postcss">
.search-form-container {
  position: relative;

  #suggestion-box {
    position: absolute;
    left: 5px;
    top: 21px;
    height: 50px;
    display: flex;
    @media (--xl) {
      top: 14px;
    }

    #prefixbox {
      font-weight: 700;
      font-style: normal;
      line-height: 1.25;
      font-size: 24px;
      color: transparent !important;

      @media (--xl) {
        font-size: 36px;
      }
    }

    #suffixbox {
      font-weight: 300;
      font-style: normal;
      line-height: 1.25;
      font-size: 24px;
      color: var(--c-secondary-neutral-2);

      @media (--xl) {
        font-size: 36px;
      }

      .is-negative & {
        color: var(--c-secondary-neutral-2);
      }
    }
  }

  #searchBox {
    display: block;
    margin-bottom: var(--spacing-2);
    padding-right: 30px;

    label {
      .label {
        .title {
          color: white !important;
          font-size: 24px !important;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 100%;
          @media (--xl) {
            font-size: 36px !important;
            top: 17px !important;
          }
        }

        .bar {
          display: none !important;
        }
      }

      input {
        font-weight: 700 !important;
        font-style: normal !important;
        font-size: 24px !important;
        line-height: 1.25 !important;
        color: var(--c-primary-neutral-3) !important;
        border-color: white !important;
        @media (--xl) {
          font-size: 36px !important;
        }

        &:focus:not([readonly]) ~ .label .title {
          top: 0 !important;
          font-size: 12px !important;
        }
      }

      &.touched .label .title {
        top: 0 !important;
        font-size: 12px !important;
      }
    }
  }

  #search-icon-box {
    position: absolute;
    top: 24px;
    right: 30px;
    cursor: pointer;
    z-index: 99;

    .dotcom-icon {
      width: 30px;
      height: 30px;
      padding: 6px;
      box-sizing: border-box;
    }

    @media (--xl) {
      top: 22px;

      .dotcom-icon {
        width: 32px;
        height: 32px;
      }
    }
  }

  .search-result-wrapper {
    position: absolute;
    z-index: 999;
    opacity: 1;
  }
}

.search-form__static-suggestions {
  margin-top: var(--spacing-6);
}

@media (--md) {
  .search-form__static-suggestions {
    margin-top: var(--spacing-8);
  }
}
</style>
